import React, { useMemo } from 'react';
import { Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { FP230Row, FP231Item, QueryResponse } from '../../../services/pwp303Api';

interface ResultTableProps {
  result: QueryResponse;
}

/** Maps F11A_15 output format code to human-readable label. */
const OUTPUT_FORMAT_LABELS: Record<number, string> = {
  1: 'CHANGE/COT/NTL/EOT',
  2: 'DRY/WET',
  3: 'WARP/WEFT',
  4: 'BEFORE WASH/3WASH',
  5: '單一結果',
};

/** Maps F11A_15 output format code to Tag color. */
const OUTPUT_FORMAT_COLORS: Record<number, string> = {
  1: 'purple',
  2: 'blue',
  3: 'cyan',
  4: 'orange',
  5: 'green',
};

/** Format a date string (ISO) into display format, or empty string. */
const fmtDate = (v: string | null): string => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('zh-TW');
};

/** Render approval status as a colored tag. */
const renderStatus = (v: string) => {
  const trimmed = (v || '').trim();
  if (!trimmed) return <Tag>-</Tag>;
  if (trimmed === 'P') return <Tag color="green">已核准</Tag>;
  if (trimmed === 'R') return <Tag color="red">退回</Tag>;
  if (trimmed === 'W') return <Tag color="orange">待審</Tag>;
  return <Tag>{trimmed}</Tag>;
};

/** Render a single test item's output format as a Tag. */
const renderFormatTag = (fmt: number | null) => {
  if (fmt == null) return <Tag>-</Tag>;
  const label = OUTPUT_FORMAT_LABELS[fmt] ?? `格式${fmt}`;
  const color = OUTPUT_FORMAT_COLORS[fmt] ?? 'default';
  return <Tag color={color}>{label}</Tag>;
};

/** Expanded row showing the FP231 test items for a report. */
const ExpandedTestItems: React.FC<{ items: FP231Item[] }> = ({ items }) => {
  if (!items || items.length === 0) {
    return <div className="text-gray-400 text-xs px-4 py-2">（無測試項目）</div>;
  }

  const itemColumns: ColumnsType<FP231Item> = [
    {
      title: '項次',
      dataIndex: 'fp231_02',
      key: 'fp231_02',
      width: 60,
      align: 'center' as const,
    },
    {
      title: '測試項目代號',
      dataIndex: 'fp231_11',
      key: 'fp231_11',
      width: 100,
    },
    {
      title: '測試項目名稱',
      dataIndex: 'f11a_11',
      key: 'f11a_11',
      width: 200,
      render: (v: string) => (v || '').trim(),
    },
    {
      title: '輸出畫面格式',
      dataIndex: 'f11a_15',
      key: 'f11a_15',
      width: 180,
      render: renderFormatTag,
    },
  ];

  return (
    <div className="px-4 py-2 bg-gray-50">
      <Table
        columns={itemColumns}
        dataSource={items.map((item, i) => ({ ...item, _key: `${item.fp231_11}-${i}` }))}
        rowKey="_key"
        size="small"
        pagination={false}
        bordered
      />
    </div>
  );
};

const columns: ColumnsType<FP230Row & { _key: string }> = [
  {
    title: '報告單號',
    dataIndex: 'fp230_01',
    key: 'fp230_01',
    width: 110,
    fixed: 'left' as const,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '繳庫單號',
    dataIndex: 'fp230_18',
    key: 'fp230_18',
    width: 110,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '項次',
    dataIndex: 'fp230_19',
    key: 'fp230_19',
    width: 60,
    align: 'center' as const,
    render: (v: number | null) => v != null ? v : '',
  },
  {
    title: '送件單號',
    dataIndex: 'fp230_13',
    key: 'fp230_13',
    width: 100,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '報告日期',
    dataIndex: 'fp230_11',
    key: 'fp230_11',
    width: 100,
    render: fmtDate,
  },
  {
    title: '測試完成日',
    dataIndex: 'fp230_12',
    key: 'fp230_12',
    width: 100,
    render: fmtDate,
  },
  {
    title: '測試人員',
    dataIndex: 'fp230_121',
    key: 'fp230_121',
    width: 80,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '色號',
    dataIndex: 'fp230_14',
    key: 'fp230_14',
    width: 60,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '色名代碼',
    dataIndex: 'fp230_16',
    key: 'fp230_16',
    width: 80,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '品號1',
    dataIndex: 'fp230_411',
    key: 'fp230_411',
    width: 100,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '品號2',
    dataIndex: 'fp230_412',
    key: 'fp230_412',
    width: 100,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '測試項目數',
    key: 'testItemCount',
    width: 90,
    align: 'center' as const,
    render: (_: unknown, row: FP230Row & { _key: string }) => {
      const count = row.testItems?.length ?? 0;
      return count > 0 ? <Tag color="blue">{count} 項</Tag> : <span className="text-gray-400">-</span>;
    },
  },
  {
    title: '狀態',
    dataIndex: 'fp230_80',
    key: 'fp230_80',
    width: 80,
    align: 'center' as const,
    render: renderStatus,
  },
  {
    title: '異動人員',
    dataIndex: 'fp230_91',
    key: 'fp230_91',
    width: 80,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '異動日期',
    dataIndex: 'fp230_92',
    key: 'fp230_92',
    width: 100,
    render: fmtDate,
  },
];

const ResultTable: React.FC<ResultTableProps> = ({ result }) => {
  const data = useMemo(
    () =>
      (result.data || []).map((row, i) => ({
        ...row,
        _key: `${row.fp230_01}-${i}`,
      })),
    [result.data]
  );

  return (
    <div>
      <div className="text-sm text-gray-500 mb-2">
        共 {result.totalCount} 筆資料（點擊列左側箭頭可展開查看測試項目）
      </div>
      <Table
        columns={columns}
        dataSource={data}
        rowKey="_key"
        size="small"
        scroll={{ x: 1500, y: 500 }}
        pagination={{
          pageSize: 50,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100'],
          showTotal: (total) => `共 ${total} 筆`,
        }}
        expandable={{
          expandedRowRender: (row) => (
            <ExpandedTestItems items={row.testItems || []} />
          ),
          rowExpandable: () => true,
        }}
      />
    </div>
  );
};

export default ResultTable;
